import React, { useEffect, useState, useContext } from 'react';
import api from '../api/axios';
import { AuthContext } from '../context/AuthContext';
import { 
    Plus, MapPin, Phone, Mail, Clock, User, Edit, Trash2, Shield, Activity, X,
    Brain, Wind, Stethoscope, Droplet, UserCheck, HeartPulse, Bone, Scissors, 
    Baby, Search, Accessibility, Thermometer, BookHeart, Heart, Users, ArrowLeft
} from 'lucide-react';

const REGIONS_DATA = {
    "г. Душанбе": ["р. Исмоили Сомони", "р. Сино", "р. Фирдавси", "р. Шохмансур"],
    "Согдийская область": ["Худжанд", "Исфара", "Истаравшан", "Канибадам", "Пенджикент", "Гулистон", "Б. Гафуров"],
    "Хатлонская область": ["Бохтар (Курган-Тюбе)", "Куляб", "Нурек", "Дангара", "Яван", "Фархор", "Восе"],
    "Районы республиканского подчинения": ["Вахдат", "Гиссар", "Турсунзаде", "Рашт", "Рогон", "Рудаки"],
    "Горно-Бадахшанская автономная область": ["Хорог", "Дарваз", "Вандж", "Рушан", "Ишкашим", "Мургаб"]
};

const formatPhone = (value) => {
    if (!value) return '+992 ';
    const numbers = value.replace(/\D/g, '');
    const tjNum = numbers.startsWith('992') ? numbers.slice(3) : numbers;
    
    let res = '+992 ';
    if (tjNum.length > 0) res += tjNum.substring(0, 3);
    if (tjNum.length > 3) res += '-' + tjNum.substring(3, 5);
    if (tjNum.length > 5) res += '-' + tjNum.substring(5, 7);
    if (tjNum.length > 7) res += '-' + tjNum.substring(7, 9);
    return res;
};

const formatAddress = (addressStr) => {
    if (!addressStr) return "Адрес не указан";
    if (addressStr.includes('|||')) {
        return addressStr.split('|||').filter(Boolean).join(', ');
    }
    return addressStr;
};

const Hospitals = () => {
    const { user } = useContext(AuthContext);
    const [hospitals, setHospitals] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [view, setView] = useState('list');
    const [activeDept, setActiveDept] = useState(null);

    const [showModal, setShowModal] = useState(false);
    const [showChiefModal, setShowChiefModal] = useState(false);

    const [currentHospital, setCurrentHospital] = useState(null);
    const [chiefCandidates, setChiefCandidates] = useState([]);
    const [selectedChiefId, setSelectedChiefId] = useState('');
    const [chiefSearch, setChiefSearch] = useState('');

    const [formData, setFormData] = useState({
        name: '', region: '', city: '', street: '', phone: '+992 ', email: '', work_hours: '8:00 - 20:00'
    });
    const [selectedDepartments, setSelectedDepartments] = useState([]);

    const [searchQuery, setSearchQuery] = useState('');
    const [filterRegion, setFilterRegion] = useState('');
    const [filterCity, setFilterCity] = useState('');
    const [visibleCount, setVisibleCount] = useState(12);

    useEffect(() => {
        setVisibleCount(12);
    }, [searchQuery, filterRegion, filterCity]);

    useEffect(() => {
        const handleScroll = () => {
            if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 200) {
                setVisibleCount(prevCount => prevCount + 12);
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const departmentOptions = [
        { name: 'Терапия', icon: <BookHeart size={24} color="#6a7282" />, desc: 'Общее лечение, первичный приём' },
        { name: 'Кардиология', icon: <Heart size={24} color="#db2777" />, desc: 'Сердце, давление, инфаркты, аритмии' },
        { name: 'Неврология', icon: <Brain size={24} color="#db2777" />, desc: 'Инсульты, головные боли, эпилепсия' },
        { name: 'Пульмонология', icon: <Wind size={24} color="#059669" />, desc: 'Лёгкие, пневмония, астма, бронхиты' },
        { name: 'Гастроэнтерология', icon: <Stethoscope size={24} color="#7c3aed" />, desc: 'Желудок, печень, кишечник' },
        { name: 'Нефрология', icon: <Droplet size={24} color="#2563eb" />, desc: 'Почки, недостаточность' },
        { name: 'Урология', icon: <UserCheck size={24} color="#dc2626" />, desc: 'Мочевыводящая система' },
        { name: 'Гинекология', icon: <Activity size={24} color="#be185d" />, desc: 'Женские органы, беременность, роды' },
        { name: 'Инфекционное отделение', icon: <Thermometer size={24} color="#ea580c" />, desc: 'Гепатит, COVID, кишечные инфекции' },
        { name: 'Травматология и ортопедия', icon: <Bone size={24} color="#4b5563" />, desc: 'Переломы, суставы, позвоночник' },
        { name: 'Хирургия', icon: <Scissors size={24} color="#0d9488" />, desc: 'Операции, швы, удаление' },
        { name: 'Онкология', icon: <Activity size={24} color="#e11d48" />, desc: 'Опухоли, рак, химиотерапия' },
        { name: 'Педиатрия', icon: <Baby size={24} color="#f59e0b" />, desc: 'Лечение детей' },
        { name: 'Реанимация (ОРИТ)', icon: <HeartPulse size={24} color="#dc2626" />, desc: 'Критические состояния' },
        { name: 'Диагностика', icon: <Search size={24} color="#6366f1" />, desc: 'УЗИ, Рентген, МРТ, КТ' },
        { name: 'Реабилитация', icon: <Accessibility size={24} color="#16a34a" />, desc: 'Восстановление после травм' },
    ];

    useEffect(() => {
        fetchHospitals();
    }, []);

    const fetchHospitals = async () => {
        setIsLoading(true);
        try {
            const res = await api.get('/hospitals');
            setHospitals(res.data);
        } catch (error) { console.error(error); } finally { setIsLoading(false); }
    };

    const toggleDepartment = (deptName) => {
        if (selectedDepartments.includes(deptName)) {
            setSelectedDepartments(selectedDepartments.filter(d => d !== deptName));
        } else {
            setSelectedDepartments([...selectedDepartments, deptName]);
        }
    };

    const openModal = (hospital = null) => {
        setCurrentHospital(hospital);
        if (hospital) {
            let region = '';
            let city = hospital.city || '';
            let street = hospital.address || '';
            
            if (hospital.address && hospital.address.includes('|||')) {
                const parts = hospital.address.split('|||');
                region = parts[0] || '';
                city = parts[1] || '';
                street = parts[2] || '';
            }

            setFormData({
                name: hospital.name, 
                region: region, 
                city: city, 
                street: street,
                phone: hospital.phone || '+992 ', 
                email: hospital.email || '', 
                work_hours: hospital.work_hours
            });
            const depts = hospital.departments ? hospital.departments.map(d => d.name) : [];
            setSelectedDepartments(depts);
        } else {
            setFormData({ name: '', region: '', city: '', street: '', phone: '+992 ', email: '', work_hours: '8:00 - 20:00' });
            setSelectedDepartments([]);
        }
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const combinedAddress = `${formData.region}|||${formData.city}|||${formData.street}`;
        
        const payload = { 
            name: formData.name,
            city: formData.city,
            address: combinedAddress,
            phone: formData.phone,
            email: formData.email,
            work_hours: formData.work_hours,
            departments: selectedDepartments 
        };

        try {
            if (currentHospital) {
                const res = await api.put(`/hospitals/${currentHospital.id}`, payload);
                setHospitals(hospitals.map(h => h.id === currentHospital.id ? res.data : h));
                if (view === 'details') setCurrentHospital(res.data);
            } else {
                const res = await api.post('/hospitals', payload);
                setHospitals([...hospitals, res.data]);
            }
            setShowModal(false);
        } catch (error) { alert("Ошибка сохранения"); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Удалить больницу?")) return;
        try {
            await api.delete(`/hospitals/${id}`);
            setHospitals(hospitals.filter(h => h.id !== id));
            if (view === 'details') setView('list');
        } catch (error) { alert("Ошибка удаления"); }
    };

    const openChiefModal = async (hospital) => {
        setCurrentHospital(hospital);
        setSelectedChiefId(hospital.chief_doctor_id || '');
        setChiefSearch('');
        try {
            const res = await api.get('/users?role=chief_doctor');
            setChiefCandidates(res.data);
            setShowChiefModal(true);
        } catch (error) { alert("Ошибка"); }
    };

    const handleAssignChief = async () => {
        try {
            const payload = {
                name: currentHospital.name,
                city: currentHospital.city,
                address: currentHospital.address,
                phone: currentHospital.phone,
                email: currentHospital.email,
                work_hours: currentHospital.work_hours,
                departments: currentHospital.departments ? currentHospital.departments.map(d => d.name) : [],
                chief_doctor_id: selectedChiefId ? parseInt(selectedChiefId) : null
            };

            await api.put(`/hospitals/${currentHospital.id}`, payload);
            fetchHospitals(); 
            setShowChiefModal(false);
        } catch (error) { alert("Ошибка при назначении"); }
    };

    const openDetails = (hospital) => {
        setCurrentHospital(hospital);
        setView('details');
        setActiveDept(null);
        window.scrollTo(0, 0);
    };

    const renderHospitalDetails = () => {
        if (!currentHospital) return null;

        const docs = (currentHospital.doctors || []).filter(d => d.role === 'doctor');
        const hospitalDeptNames = currentHospital.departments ? currentHospital.departments.map(d => d.name) : [];
        const visibleDepartments = departmentOptions.filter(opt => hospitalDeptNames.includes(opt.name));

        return (
            <div style={{ maxWidth: '1280px', paddingBottom: '40px' }}>
                <style>{`
                    .badge-black { background-color: #0F172A; color: #F8FAFC; padding: 2px 8px; border-radius: 6px; font-size: 12px; font-weight: 500; }
                    .badge-gray { background-color: #F1F5F9; color: #475569; padding: 2px 8px; border-radius: 6px; font-size: 12px; font-weight: 500; }
                    .card-active { border-color: #3B82F6 !important; box-shadow: 0 0 0 1px #3B82F6 !important; }
                    .doc-card-hover:hover { background-color: #F1F5F9; }
                `}</style>

                <div className="card" style={{ marginBottom: '24px', border: '1px solid #E5E7EB', borderRadius: '12px', overflow: 'hidden' }}>
                    <div style={{ padding: '24px', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                            <div style={{ padding: '12px', backgroundColor: '#EFF6FF', borderRadius: '8px', color: '#2563EB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Activity size={24} />
                            </div>
                            <div>
                                <h1 style={{ fontSize: '24px', fontWeight: '600', lineHeight: '1.2', margin: 0, color: '#0F172A' }}>{currentHospital.name}</h1>
                                <p style={{ color: '#64748B', fontSize: '14px', marginTop: '4px', margin: 0 }}>{formatAddress(currentHospital.address)}</p>
                            </div>
                        </div>
                        <button 
                            onClick={() => setView('list')} 
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '14px', fontWeight: '500', color: '#0F172A', cursor: 'pointer' }}
                        >
                            <ArrowLeft size={16} /> Назад
                        </button>
                    </div>
                    <div style={{ padding: '0 24px 24px', display: 'flex', gap: '24px', fontSize: '14px', color: '#475569' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Users size={20} color="#64748B"/>
                            <span style={{ fontWeight: '600', color: '#0F172A' }}>{docs.length}</span> врачей
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Activity size={20} color="#64748B"/>
                            <span style={{ fontWeight: '600', color: '#0F172A' }}>{visibleDepartments.length}</span> отделений
                        </div>
                    </div>
                </div>

                <div>
                    <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: '#0F172A' }}>Отделения и врачи</h2>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '16px' }}>
                        
                        {visibleDepartments.map(dept => {
                            const deptDocs = docs.filter(d => d.department === dept.name);
                            const count = deptDocs.length;
                            const isActive = activeDept === dept.name;

                            return (
                                <div 
                                    key={dept.name} 
                                    className={isActive ? 'card-active' : ''}
                                    onClick={() => setActiveDept(isActive ? null : dept.name)}
                                    style={{ 
                                        background: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', 
                                        padding: '24px', cursor: 'pointer', transition: 'all 0.2s ease',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <div style={{background: isActive ? '#eff6ff' : 'transparent', padding: '6px 6px 2px', borderRadius: '6px'}}>{dept.icon}</div>
                                            <h3 style={{ fontSize: '16px', fontWeight: '500', margin: 0, color: '#0F172A' }}>{dept.name}</h3>
                                        </div>
                                        <span className={count > 0 ? "badge-black" : "badge-gray"}>
                                            {count} врачей
                                        </span>
                                    </div>
                                    <p style={{ color: '#64748B', fontSize: '12px', lineHeight: '1.5', margin: 0 }}>{dept.desc}</p>

                                    {isActive && (
                                        <div style={{ marginTop: '20px', borderTop: '1px solid #E5E7EB', paddingTop: '16px', animation: 'fadeIn 0.2s' }}>
                                            {count > 0 ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                    {deptDocs.map(doc => (
                                                        <div key={doc.id} className="doc-card-hover" style={{ backgroundColor: '#F8FAFC', borderRadius: '8px', padding: '12px', display: 'flex', gap: '12px', transition: 'background 0.2s' }}>
                                                            <div style={{ color: '#2563EB' }}><User size={20}/></div>
                                                            <div>
                                                                <p style={{ fontWeight: '500', fontSize: '14px', color: '#0F172A', margin: 0 }}>{doc.full_name}</p>
                                                                <p style={{ fontSize: '12px', color: '#64748B', margin: '2px 0 0' }}>{doc.specialization}</p>
                                                                <p style={{ fontSize: '12px', color: '#64748B', margin: 0 }}>Каб: {doc.cabinet} • {doc.phone}</p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div style={{ textAlign: 'center', color: '#64748B', fontSize: '14px', padding: '10px 0' }}>
                                                    В этом отделении пока нет врачей
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div style={{ marginTop: '24px', backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '12px', padding: '24px' }}>
                    <h4 style={{ color: '#1E3A8A', fontSize: '16px', fontWeight: '500', marginBottom: '8px', margin: '0 0 8px 0' }}>ℹ️ Как это работает</h4>
                    <div style={{ color: '#1E40AF', fontSize: '14px', lineHeight: '1.6' }}>
                        <p style={{ margin: 0 }}>• Нажмите на отделение, чтобы увидеть список врачей</p>
                        <p style={{ margin: 0 }}>• При регистрации врача выбирается отделение, к которому он относится</p>
                        <p style={{ margin: 0 }}>• Список отделений стандартизирован для всех больниц</p>
                    </div>
                </div>
            </div>
        );
    };

    const filteredHospitals = hospitals.filter(h => {
        // ЖЕСТКИЙ ФИЛЬТР ДЛЯ ГЛАВВРАЧА: Он видит только СВОЮ больницу
        if (user?.role === 'chief_doctor') {
            if (h.chief_doctor_id !== user.id) {
                return false;
            }
        }

        const searchLower = searchQuery.toLowerCase();
        const matchSearch = h.name.toLowerCase().includes(searchLower) || 
                            (h.address && h.address.toLowerCase().includes(searchLower)) ||
                            (h.phone && h.phone.toLowerCase().includes(searchLower));
        
        let matchRegion = true;
        let matchCity = true;

        if (h.address && h.address.includes('|||')) {
            const parts = h.address.split('|||');
            const hRegion = parts[0] || '';
            const hCity = parts[1] || '';
            
            if (filterRegion) matchRegion = hRegion === filterRegion;
            if (filterCity) matchCity = hCity === filterCity;
        } else if (filterRegion || filterCity) {
            matchRegion = false; 
        }

        return matchSearch && matchRegion && matchCity;
    });

    const visibleHospitals = filteredHospitals.slice(0, visibleCount);

    if (view === 'details') {
        return renderHospitalDetails();
    }

    return (
        <div>
            <div className="page-header-row" style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'stretch' }}>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                    <div>
                        <h2 className="page-title">Больницы</h2>
                        
                        {/* СКРЫВАЕМ СЧЕТЧИК ОТ ГЛАВВРАЧА */}
                        {user?.role !== 'chief_doctor' && (
                            <div className="header-meta">
                                <span>Найдено: {filteredHospitals.length} из {hospitals.length}</span>
                            </div>
                        )}
                    </div>
                    {user?.role === 'super_admin' && (
                        <button className="btn-primary" onClick={() => openModal()}>
                            <Plus size={16} style={{marginRight: '8px'}}/> Добавить больницу
                        </button>
                    )}
                </div>

                {/* СКРЫВАЕМ ФИЛЬТРЫ И ПОИСК ОТ ГЛАВВРАЧА */}
                {user?.role !== 'chief_doctor' && (
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', background: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                        <div style={{ flex: 1, minWidth: '250px', position: 'relative' }}>
                            <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                            <input 
                                type="text" 
                                placeholder="Поиск по названию, телефону или улице..." 
                                value={searchQuery} 
                                onChange={e => setSearchQuery(e.target.value)} 
                                style={{ width: '100%', padding: '10px 12px 10px 38px', borderRadius: '8px', border: '1px solid #e5e7eb', outline: 'none', fontSize: '0.9rem' }} 
                            />
                        </div>
                        <div style={{ minWidth: '220px' }}>
                            <select 
                                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', outline: 'none', fontSize: '0.9rem', backgroundColor: '#fff', cursor: 'pointer' }} 
                                value={filterRegion} 
                                onChange={e => { setFilterRegion(e.target.value); setFilterCity(''); }}
                            >
                                <option value="">Все регионы</option>
                                {Object.keys(REGIONS_DATA).map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                        </div>
                        <div style={{ minWidth: '220px' }}>
                            <select 
                                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', outline: 'none', fontSize: '0.9rem', backgroundColor: filterRegion ? '#fff' : '#f3f4f6', cursor: filterRegion ? 'pointer' : 'not-allowed' }} 
                                value={filterCity} 
                                onChange={e => setFilterCity(e.target.value)} 
                                disabled={!filterRegion}
                            >
                                <option value="">Все города/районы</option>
                                {filterRegion && REGIONS_DATA[filterRegion].map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                    </div>
                )}

            </div>

            {/* МЕНЯЕМ СЕТКУ ДЛЯ ГЛАВВРАЧА НА БОЛЬШУЮ КАРТОЧКУ ПО ЦЕНТРУ */}
            <div 
                className={user?.role === 'chief_doctor' ? "" : "hospitals-grid"} 
                style={user?.role === 'chief_doctor' ? { display: 'flex', justifyContent: 'center', padding: '20px 0' } : {}}
            >
                {isLoading ? (
                    <p style={{padding: '20px', color: '#64748b'}}>Загрузка...</p>
                ) : visibleHospitals.length === 0 ? (
                    <div style={{ padding: '40px 20px', textAlign: 'center', width: '100%', gridColumn: '1 / -1' }}>
                        <Activity size={48} color="#cbd5e1" style={{marginBottom: '16px'}} />
                        <p style={{color: '#64748b', fontSize: '1.1rem'}}>По вашему запросу ничего не найдено</p>
                    </div>
                ) : visibleHospitals.map(h => (
                    <div 
                        key={h.id} 
                        className="hospital-card" 
                        style={user?.role === 'chief_doctor' ? { width: '100%', maxWidth: '800px', transform: 'scale(1.02)' } : {}}
                    >
                        <div className="h-card-header">
                            <div className="h-icon-box"><Activity size={24} color="#3b82f6"/></div>
                            <div className="h-title-box">
                                <h4>{h.name}</h4>
                                <p>{h.city || "Город не указан"}</p>
                            </div>
                        </div>
                        
                        <div className="h-card-body">
                            <div className="info-row"><MapPin size={16} /> <span title={formatAddress(h.address)}>{formatAddress(h.address)}</span></div>
                            <div className="info-row"><Phone size={16} /> <span>{h.phone || "Нет телефона"}</span></div>
                            <div className="info-row"><Mail size={16} /> <span>{h.email || "Нет Email"}</span></div>
                            <div className="info-row"><Clock size={16} /> <span>{h.work_hours || "Не указано"}</span></div>
                            <div className="info-row"><Users size={16} /> <span>Врачей: {h.doctors ? h.doctors.filter(d => d.role === 'doctor').length : 0}</span></div>
                            
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '15px' }}>
                                <User size={20} className={h.chief_doctor ? "text-green" : "text-orange"} />
                                <div>
                                    <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0' }}>Главврач:</p>
                                    {h.chief_doctor ? (
                                        <p style={{ margin: '0', fontWeight: '500', color: '#16a34a' }}>{h.chief_doctor.full_name}</p>
                                    ) : (
                                        <p style={{ margin: '0', fontWeight: '500', color: '#ea580c' }}>Не назначен</p>
                                    )}
                                </div>
                            </div>
                            
                            <div style={{fontSize:'0.8rem', color:'#888', marginTop:'5px', marginLeft:'2px'}}>
                                Отделений: <b>{h.departments ? h.departments.length : 0}</b>
                            </div>
                        </div>

                        <div className="h-card-footer">
                            <button className="btn-primary btn-full" onClick={() => openDetails(h)}>
                                <Activity size={16} style={{marginRight: '8px'}}/> Открыть больницу
                            </button>
                            <div className="h-actions-row">
                                <button className="btn-outline" style={{ flex: 1 }} onClick={() => openModal(h)}>
                                    <Edit size={14} style={{marginRight: '4px'}}/> Изменить
                                </button>
                                
                                {/* Кнопку "Главврач" видят все КРОМЕ chief_doctor */}
                                {user?.role !== 'chief_doctor' && (
                                    <button className="btn-outline" style={{ flex: 1 }} onClick={() => openChiefModal(h)}>
                                        <Shield size={14} style={{marginRight: '4px'}}/> Главврач
                                    </button>
                                )}

                                {user?.role === 'super_admin' && (
                                    <button className="btn-outline btn-icon-only btn-delete" onClick={() => handleDelete(h.id)}>
                                        <Trash2 size={16} color="red"/>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {!isLoading && visibleHospitals.length < filteredHospitals.length && (
                <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                    Прокрутите вниз, чтобы загрузить еще...
                </div>
            )}

            {/* Модалка изменения/добавления больницы */}
            {showModal && (
                <div style={modalOverlayStyle} onClick={() => setShowModal(false)}>
                    <div className="modal-content-lg" onClick={e => e.stopPropagation()} style={lgModalStyle}>
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'20px'}}>
                            <div>
                                <h4 style={{fontSize:'22px', fontWeight:'500', display:'flex', alignItems:'center', gap:'15px', margin:'0'}}>
                                    <span style={{background:'#eff6ff', padding:'10px 12px 6px', borderRadius:'8px', color:'#2563eb'}}><Activity size={24}/></span>
                                    <div>
                                        {currentHospital ? 'Изменить данные больницы' : 'Добавить больницу'}
                                        <p style={{fontSize:'14px', fontWeight:'400', color:'#6b7280'}}>Заполните информацию и выберите отделения</p>
                                    </div>
                                </h4>
                            </div>
                            <button onClick={() => setShowModal(false)} style={{background:'none', border:'none', cursor:'pointer'}}><X size={24} color="#9ca3af"/></button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div style={{marginBottom:'16px'}}>
                                <label style={labelStyle}>Название больницы *</label>
                                <input className="form-input" style={inputStyle} value={formData.name} onChange={e=>setFormData({...formData, name: e.target.value})} placeholder="Городская Больница №1" required />
                            </div>
                            
                            <div style={{marginBottom:'16px'}}>
                                <label style={labelStyle}>Область / Регион *</label>
                                <select className="form-input" style={inputStyle} value={formData.region} onChange={e=>setFormData({...formData, region: e.target.value, city: '', street: ''})} required>
                                    <option value="">-- Выберите регион --</option>
                                    {Object.keys(REGIONS_DATA).map(r => <option key={r} value={r}>{r}</option>)}
                                </select>
                            </div>
                            
                            <div style={{marginBottom:'16px'}}>
                                <label style={labelStyle}>Город / район *</label>
                                <select className="form-input" style={inputStyle} value={formData.city} onChange={e=>setFormData({...formData, city: e.target.value, street: ''})} disabled={!formData.region} required>
                                    <option value="">-- Выберите город/район --</option>
                                    {formData.region && REGIONS_DATA[formData.region].map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            
                            <div style={{marginBottom:'16px'}}>
                                <label style={labelStyle}>Улица, дом, квартира *</label>
                                <input className="form-input" style={inputStyle} value={formData.street} onChange={e=>setFormData({...formData, street: e.target.value})} disabled={!formData.city} placeholder="ул. Рудаки, 123" required />
                            </div>

                            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'15px', marginBottom:'16px'}}>
                                <div>
                                    <label style={labelStyle}>Телефон</label>
                                    <input className="form-input" style={inputStyle} value={formData.phone} onChange={e=>setFormData({...formData, phone: formatPhone(e.target.value)})} placeholder="+992 XXX-XX-XX-XX" />
                                </div>
                                <div>
                                    <label style={labelStyle}>Email</label>
                                    <input className="form-input" style={inputStyle} type="email" value={formData.email} onChange={e=>setFormData({...formData, email: e.target.value})} placeholder="mail@example.tj" />
                                </div>
                            </div>
                            
                            <div style={{marginBottom:'24px'}}>
                                <label style={labelStyle}>Часы работы</label>
                                <input className="form-input" style={inputStyle} value={formData.work_hours} onChange={e=>setFormData({...formData, work_hours: e.target.value})} placeholder="8:00 - 20:00" />
                            </div>

                            <div style={{borderTop:'1px solid #eee', paddingTop:'20px'}}>
                                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'15px'}}>
                                    <label style={labelStyle}>Отделения</label>
                                    <div style={{display:'flex', gap:'10px', alignItems:'center'}}>
                                        <button type="button" onClick={() => setSelectedDepartments([])} style={{fontSize:'0.8rem', color:'#ef4444', background:'none', border:'none', cursor:'pointer'}}>Снять выбор</button>
                                        <div style={{fontSize:'0.9rem', color:'#6b7280', background:'#f3f4f6', padding:'2px 8px', borderRadius:'12px'}}>Выбрано: {selectedDepartments.length}</div>
                                    </div>
                                </div>
                                
                                <div style={{display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:'12px', maxHeight:'250px', overflowY:'auto', paddingRight:'5px', marginBottom:'20px', border: '1px solid #f3f4f6', borderRadius: '8px', padding: '10px'}}>
                                    {departmentOptions.map(dept => {
                                        const isSelected = selectedDepartments.includes(dept.name);
                                        return (
                                            <div key={dept.name} onClick={() => toggleDepartment(dept.name)} style={{border: isSelected ? '1px solid #3b82f6' : '1px solid #e5e7eb', background: isSelected ? '#eff6ff' : 'white', borderRadius: '8px', padding: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px', transition: 'all 0.2s', position: 'relative'}}>
                                                <div style={{background: isSelected ? '#dbeafe' : '#f3f4f6', padding:'6px', borderRadius:'6px', display:'flex'}}>{dept.icon}</div>
                                                <div>
                                                    <p style={{fontWeight:'600', fontSize:'0.85rem', color:'#111827', margin:0}}>{dept.name}</p>
                                                    <p style={{fontSize:'0.75rem', color:'#6b7280', margin:0}}>{dept.desc}</p>
                                                </div>
                                                {isSelected && <div style={{position:'absolute', top:'10px', right:'10px', width:'10px', height:'10px', background:'#2563eb', borderRadius:'50%'}}></div>}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div style={{display:'flex', gap:'15px', marginTop:'20px', paddingTop:'20px', borderTop:'1px solid #eee'}}>
                                <button type="button" onClick={() => setShowModal(false)} style={cancelBtnStyle}>Отмена</button>
                                <button type="submit" style={submitBtnStyle}>{currentHospital ? 'Сохранить изменения' : 'Создать больницу'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Модалка назначения главврача */}
            {showChiefModal && (
                <div style={modalOverlayStyle} onClick={() => setShowChiefModal(false)}>
                    <div className="card" onClick={e=>e.stopPropagation()} style={{width:'400px', padding:'24px'}}>
                        <h3 style={{fontWeight:'bold', margin:'0', position: 'relative'}}>
                            Назначение главврача
                            <button onClick={() => setShowChiefModal(false)} style={{background:'none', border:'none', cursor:'pointer', position:'absolute', right:'0'}}><X size={24} color="#9ca3af"/></button>
                        </h3>
                        <p style={{fontSize:'0.9rem', marginBottom: '16px'}}>Больница: <b>{currentHospital?.name}</b></p>
                        
                        <div style={{ position: 'relative', marginBottom: '12px' }}>
                            <Search size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                            <input 
                                className="form-input" 
                                style={{...inputStyle, paddingLeft: '34px'}} 
                                placeholder="Поиск по ФИО..." 
                                value={chiefSearch}
                                onChange={e => setChiefSearch(e.target.value)}
                            />
                        </div>

                        <select 
                            className="form-input" 
                            style={{...inputStyle, height: '150px'}} 
                            value={selectedChiefId} 
                            onChange={e => setSelectedChiefId(e.target.value)}
                            size={6}
                        >
                            <option value="">-- Снять назначение (Свободен) --</option>
                            {chiefCandidates
                                .filter(c => 
                                    (!c.hospital_id || c.hospital_id === currentHospital.id) &&
                                    (c.full_name || '').toLowerCase().includes(chiefSearch.toLowerCase())
                                )
                                .slice(0, 5)
                                .map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)
                            }
                        </select>
                        <button onClick={handleAssignChief} style={{...submitBtnStyle, width:'100%', marginTop:'16px'}}>Сохранить</button>
                    </div>
                </div>
            )}

        </div>
    );
};

const modalOverlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(3px)' };
const lgModalStyle = { backgroundColor: 'white', borderRadius: '16px', width: '700px', maxWidth: '95%', maxHeight: '90vh', overflowY: 'auto', padding: '32px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', position: 'relative' };
const labelStyle = { display: 'block', fontSize: '0.9rem', fontWeight: '600', color: '#374151', marginBottom: '6px' };
const inputStyle = { width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#f9fafb', fontSize: '0.95rem', color: '#111827', outline: 'none' };
const cancelBtnStyle = { flex: 1, padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', background: 'white', color: '#374151', fontWeight: '500', cursor: 'pointer' };
const submitBtnStyle = { flex: 2, padding: '12px', borderRadius: '8px', border: 'none', background: '#2563eb', color: 'white', fontWeight: '500', cursor: 'pointer' };

export default Hospitals;